import { Layout } from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Search, Eye, ShoppingCart, IndianRupee, TrendingUp, Hash, User, MapPin, Calendar, Wallet, Phone, Package, Loader2 } from "lucide-react";
import { useState, useMemo } from "react";
import { DateRangeFilter } from "@/components/DateRangeFilter";
import { startOfToday, startOfYesterday, startOfMonth, format } from "date-fns";
import { useOrders, useOrderDetails } from "@/lib/api/hooks/useOrders";
import { authService } from "@/lib/api/services/auth.service";

// API Response type
interface OrderApiResponse {
  orderNo: number;
  orderDate: string;
  partyCode: string;
  partyName: string;
  city: string;
  mobileNumber: string;
  totalAmount: number;
  itemsCount: number;
  salesManInfo: string;
  orderType?: string;
}

// Transformed Order type for component
interface TransformedOrder {
  id: string;
  date: string;
  time: string;
  customer: string;
  town: string;
  mobile: string;
  amount: number;
  items: number;
  orderNo: number;
  partyCode: string;
  salesManInfo: string;
  salesManCode: string;
  orderType?: string;
  details: Array<{
    code: string;
    name: string;
    qty: number;
    free: number;
    rate: number;
    value: number;
    scheme: string;
  }>;
}

// Order Details API Response type
interface OrderDetailItem {
  firmId: number;
  firmName: string;
  partyCode: string;
  partyName: string;
  totalAmount: number;
  productCode: string;
  productName: string;
  qty: number;
  free: number;
  scheme: string;
  rate: number;
  discount: number;
  amount: number;
  remarks: string;
  salesManCode: string;
  deliveryMode: string;
  orderNo: number;
  orderDate: string;
  city: string;
  mobileNumber: string;
}

const Orders = () => {
  const [search, setSearch] = useState("");
  const [dateFilter, setDateFilter] = useState<"today" | "yesterday" | "mtd" | "custom">("today");
  const [dateRange, setDateRange] = useState<{ from: Date | undefined; to: Date | undefined }>({ from: undefined, to: undefined });
  // 0 = ALL, 1 = Pending, 2 = Bill (default to Pending)
  const [status, setStatus] = useState<number>(1);
  // Order Type: null = All, "Customer" = Customer, "SalesMan" = SalesMan (default to null/All)
  const [orderType, setOrderType] = useState<string | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<TransformedOrder | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // Get firmId from user
  const user = authService.getCurrentUser();
  const firmId = user?.firmId;

  // Helper function to format date as YYYY-MM-DD HH:mm:ss in local time
  // This ensures the local date/time is preserved without UTC conversion
  const formatLocalDateTime = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };

  // Calculate date range for API
  const { fromDate, toDate } = useMemo(() => {
    const now = new Date();
    let fromDate: Date;
    let toDate: Date = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);

    switch (dateFilter) {
      case "today":
        fromDate = startOfToday();
        toDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
        break;
      case "yesterday":
        fromDate = startOfYesterday();
        toDate = new Date(fromDate.getFullYear(), fromDate.getMonth(), fromDate.getDate(), 23, 59, 59, 999);
        break;
      case "mtd":
        fromDate = startOfMonth(now);
        toDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
        break;
      case "custom":
        if (dateRange.from && dateRange.to) {
          fromDate = new Date(dateRange.from);
          fromDate.setHours(0, 0, 0, 0);
          toDate = new Date(dateRange.to);
          toDate.setHours(23, 59, 59, 999);
        } else {
          fromDate = startOfToday();
          toDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
        }
        break;
      default:
        fromDate = startOfToday();
        toDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
    }

    return {
      fromDate: formatLocalDateTime(fromDate),
      toDate: formatLocalDateTime(toDate),
    };
  }, [dateFilter, dateRange]);

  // Fetch orders from API
  const { data: ordersResponse, isLoading, error } = useOrders({
    firmId,
    fromDate,
    toDate,
    status,
    orderType,
  });

  // Transform API response to component format
  const orders: TransformedOrder[] = useMemo(() => {
    if (!ordersResponse) return [];
    
    // Handle both array response and wrapped response
    const ordersArray = Array.isArray(ordersResponse) 
      ? ordersResponse 
      : (ordersResponse as any)?.orders || [];

    return ordersArray.map((order: OrderApiResponse): TransformedOrder => {
      const orderDate = new Date(order.orderDate);
      const dateStr = format(orderDate, "yyyy-MM-dd");
      const timeStr = format(orderDate, "h:mm a");

      return {
        id: `ORD-${order.orderNo}`,
        date: dateStr,
        time: timeStr,
        customer: order.partyName,
        town: order.city,
        mobile: order.mobileNumber,
        amount: order.totalAmount,
        items: order.itemsCount,
        orderNo: order.orderNo,
        partyCode: order.partyCode,
        salesManInfo: order.salesManInfo || "",
        salesManCode: order.salesManInfo || "",
        orderType: order.orderType,
        details: [], // Will be fetched from API when dialog opens
      };
    });
  }, [ordersResponse]);

  const handleDateFilterChange = (
    filter: "today" | "yesterday" | "mtd" | "custom", 
    range?: { from: Date | undefined; to: Date | undefined }
  ) => {
    setDateFilter(filter);
    if (range) {
      setDateRange(range);
    }
  };

  const filteredOrders = orders.filter(
    (order) =>
      (order.id.toLowerCase().includes(search.toLowerCase()) ||
      order.customer.toLowerCase().includes(search.toLowerCase()))
  );

  // Utility functions
  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return format(date, "yyyy-MM-dd");
    } catch {
      return dateString;
    }
  };

  const formatTime = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return format(date, "h:mm a");
    } catch {
      return dateString;
    }
  };

  const formatCurrency = (amount: number) => {
    return `₹${amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Get badge className for orderType
  const getOrderTypeBadgeClassName = (orderType: string | undefined) => {
    if (!orderType) return "";
    return orderType === "Customer" 
      ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 border-blue-200 dark:border-blue-800" 
      : "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800";
  };

  // Lakhs-only formatter for summary cards (Total Value, Avg Order Value)
  const formatLakhsCompact = (amount: number): string => {
    const value = Number.isFinite(amount) ? amount : 0;
    const absValue = Math.abs(value);
    const sign = value < 0 ? '-' : '';
    const lakhs = absValue / 100000;
    return `${sign}₹${lakhs.toFixed(2)} L`;
  };

  const handleViewDetails = (order: TransformedOrder) => {
    setSelectedOrder(order);
    setIsDialogOpen(true);
  };

  // Fetch order details when dialog is open
  const { data: orderDetails, isLoading: isLoadingDetails } = useOrderDetails(
    {
      firmId,
      orderNo: selectedOrder?.orderNo || 0,
      partyCode: selectedOrder?.partyCode,
      salesManCode: selectedOrder?.salesManCode,
    },
    isDialogOpen && !!selectedOrder
  );

  return (
    <Layout>
      <div className="flex flex-col h-[calc(100vh-8rem)] overflow-hidden">
        <div className="flex-shrink-0 space-y-3 pb-3 -mt-4 px-2 sm:px-0">
          <div className="flex flex-col gap-3">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
              <h2 className="text-2xl sm:text-3xl font-bold mt-2">Orders</h2>
              <div className="relative w-full sm:w-auto sm:max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search orders..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10 w-full sm:w-auto"
                />
              </div>
            </div>
            <DateRangeFilter
              onFilterChange={handleDateFilterChange}
              onStatusChange={(newStatus) => setStatus(newStatus)}
              onOrderTypeChange={(newOrderType) => setOrderType(newOrderType)}
            />
          </div>

          <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
          {/* Total Orders Card */}
          <Card className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-background/95 via-background/90 to-muted/50 backdrop-blur-xl border border-white/10 dark:border-white/5 hover:border-white/20 dark:hover:border-white/10 transition-all duration-500 ease-out hover:-translate-y-1 hover:shadow-[0_20px_50px_-12px_rgba(0,114,181,0.4)]">
            {/* Animated gradient border */}
            <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-gradient-to-r from-[#0072B5] via-[#0090B5] to-[#00B5B0] p-[1px]">
              <div className="absolute inset-[1px] rounded-2xl bg-background/95" />
            </div>
            {/* Background glow orbs */}
            <div className="absolute -top-20 -right-20 w-40 h-40 rounded-full blur-3xl opacity-0 group-hover:opacity-60 transition-all duration-700" style={{ background: 'radial-gradient(circle, rgba(0,114,181,0.15), transparent 70%)' }} />
            <div className="absolute -bottom-10 -left-10 w-32 h-32 rounded-full blur-2xl opacity-0 group-hover:opacity-40 transition-all duration-700 delay-100" style={{ background: 'radial-gradient(circle, rgba(0,114,181,0.15), transparent 70%)' }} />
            {/* Mesh pattern overlay */}
            <div className="absolute inset-0 opacity-[0.02] group-hover:opacity-[0.04] transition-opacity duration-500" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)', backgroundSize: '24px 24px' }} />
            <div className="relative p-3 sm:p-5">
              <div className="flex items-start justify-between gap-2 sm:gap-4">
                <div className="relative">
                  <div className="absolute inset-0 rounded-2xl blur-xl opacity-40 group-hover:opacity-70 transition-opacity duration-500 bg-gradient-to-br from-[#0072B5] via-[#0090B5] to-[#00B5B0]" />
                  <div className="relative p-2.5 sm:p-3.5 rounded-xl sm:rounded-2xl bg-gradient-to-br from-[#0072B5] via-[#0090B5] to-[#00B5B0] shadow-lg ring-1 ring-white/25 group-hover:scale-110 group-hover:rotate-[-6deg] transition-all duration-500 ease-out">
                    <ShoppingCart className="h-4 w-4 sm:h-5 sm:w-5 text-white drop-shadow-md" strokeWidth={2.5} />
                  </div>
                </div>
                <div className="flex-1 text-right min-w-0">
                  <p className="text-[10px] sm:text-xs font-medium text-muted-foreground/70 uppercase tracking-wider mb-0.5 sm:mb-1">Total Orders</p>
                  <div className="text-lg sm:text-xl md:text-2xl font-bold tracking-tight bg-gradient-to-r from-[#0072B5] to-[#00B5B0] bg-clip-text text-transparent">
                    {isLoading ? "..." : orders.length}
                  </div>
                </div>
              </div>
              <div className="mt-3 sm:mt-4 flex items-center gap-2">
                <div className="flex-1 h-1 rounded-full bg-gradient-to-r from-[#0072B5] via-[#0090B5] to-[#00B5B0] opacity-20 group-hover:opacity-40 transition-opacity duration-500" />
                <div className="w-1.5 h-1.5 rounded-full bg-gradient-to-r from-[#0072B5] to-[#00B5B0] animate-pulse" />
              </div>
            </div>
          </Card>
          
          {/* Total Value Card */}
          <Card className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-background/95 via-background/90 to-muted/50 backdrop-blur-xl border border-white/10 dark:border-white/5 hover:border-white/20 dark:hover:border-white/10 transition-all duration-500 ease-out hover:-translate-y-1 hover:shadow-[0_20px_50px_-12px_rgba(16,185,129,0.4)]">
            <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 p-[1px]">
              <div className="absolute inset-[1px] rounded-2xl bg-background/95" />
            </div>
            <div className="absolute -top-20 -right-20 w-40 h-40 rounded-full blur-3xl opacity-0 group-hover:opacity-60 transition-all duration-700" style={{ background: 'radial-gradient(circle, rgba(16,185,129,0.15), transparent 70%)' }} />
            <div className="absolute -bottom-10 -left-10 w-32 h-32 rounded-full blur-2xl opacity-0 group-hover:opacity-40 transition-all duration-700 delay-100" style={{ background: 'radial-gradient(circle, rgba(16,185,129,0.15), transparent 70%)' }} />
            <div className="absolute inset-0 opacity-[0.02] group-hover:opacity-[0.04] transition-opacity duration-500" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)', backgroundSize: '24px 24px' }} />
            <div className="relative p-3 sm:p-5">
              <div className="flex items-start justify-between gap-2 sm:gap-4">
                <div className="relative">
                  <div className="absolute inset-0 rounded-2xl blur-xl opacity-40 group-hover:opacity-70 transition-opacity duration-500 bg-gradient-to-br from-emerald-500 via-emerald-600 to-teal-600" />
                  <div className="relative p-2.5 sm:p-3.5 rounded-xl sm:rounded-2xl bg-gradient-to-br from-emerald-500 via-emerald-600 to-teal-600 shadow-lg ring-1 ring-white/25 group-hover:scale-110 group-hover:rotate-[-6deg] transition-all duration-500 ease-out">
                    <IndianRupee className="h-4 w-4 sm:h-5 sm:w-5 text-white drop-shadow-md" strokeWidth={2.5} />
                  </div>
                </div>
                <div className="flex-1 text-right min-w-0">
                  <p className="text-[10px] sm:text-xs font-medium text-muted-foreground/70 uppercase tracking-wider mb-0.5 sm:mb-1">Total Value</p>
                  <div className="text-lg sm:text-xl md:text-2xl font-bold tracking-tight bg-gradient-to-r from-emerald-500 to-teal-600 bg-clip-text text-transparent">
                    {isLoading ? "..." : formatLakhsCompact(orders.reduce((sum, order) => sum + order.amount, 0))}
                  </div>
                </div>
              </div>
              <div className="mt-3 sm:mt-4 flex items-center gap-2">
                <div className="flex-1 h-1 rounded-full bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 opacity-20 group-hover:opacity-40 transition-opacity duration-500" />
                <div className="w-1.5 h-1.5 rounded-full bg-gradient-to-r from-emerald-500 to-teal-600 animate-pulse" />
              </div>
            </div>
          </Card>
          
          {/* Avg Order Value Card */}
          <Card className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-background/95 via-background/90 to-muted/50 backdrop-blur-xl border border-white/10 dark:border-white/5 hover:border-white/20 dark:hover:border-white/10 transition-all duration-500 ease-out hover:-translate-y-1 hover:shadow-[0_20px_50px_-12px_rgba(139,92,246,0.4)]">
            <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-gradient-to-r from-violet-500 via-purple-500 to-fuchsia-500 p-[1px]">
              <div className="absolute inset-[1px] rounded-2xl bg-background/95" />
            </div>
            <div className="absolute -top-20 -right-20 w-40 h-40 rounded-full blur-3xl opacity-0 group-hover:opacity-60 transition-all duration-700" style={{ background: 'radial-gradient(circle, rgba(139,92,246,0.15), transparent 70%)' }} />
            <div className="absolute -bottom-10 -left-10 w-32 h-32 rounded-full blur-2xl opacity-0 group-hover:opacity-40 transition-all duration-700 delay-100" style={{ background: 'radial-gradient(circle, rgba(139,92,246,0.15), transparent 70%)' }} />
            <div className="absolute inset-0 opacity-[0.02] group-hover:opacity-[0.04] transition-opacity duration-500" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)', backgroundSize: '24px 24px' }} />
            <div className="relative p-3 sm:p-5">
              <div className="flex items-start justify-between gap-2 sm:gap-4">
                <div className="relative">
                  <div className="absolute inset-0 rounded-2xl blur-xl opacity-40 group-hover:opacity-70 transition-opacity duration-500 bg-gradient-to-br from-violet-500 via-purple-600 to-fuchsia-600" />
                  <div className="relative p-2.5 sm:p-3.5 rounded-xl sm:rounded-2xl bg-gradient-to-br from-violet-500 via-purple-600 to-fuchsia-600 shadow-lg ring-1 ring-white/25 group-hover:scale-110 group-hover:rotate-[-6deg] transition-all duration-500 ease-out">
                    <TrendingUp className="h-4 w-4 sm:h-5 sm:w-5 text-white drop-shadow-md" strokeWidth={2.5} />
                  </div>
                </div>
                <div className="flex-1 text-right min-w-0">
                  <p className="text-[10px] sm:text-xs font-medium text-muted-foreground/70 uppercase tracking-wider mb-0.5 sm:mb-1">Avg Order Value</p>
                  <div className="text-lg sm:text-xl md:text-2xl font-bold tracking-tight bg-gradient-to-r from-violet-500 to-fuchsia-600 bg-clip-text text-transparent">
                    {isLoading ? "..." : formatLakhsCompact(orders.length > 0 ? orders.reduce((sum, order) => sum + order.amount, 0) / orders.length : 0)}
                  </div>
                </div>
              </div>
              <div className="mt-3 sm:mt-4 flex items-center gap-2">
                <div className="flex-1 h-1 rounded-full bg-gradient-to-r from-violet-500 via-purple-500 to-fuchsia-500 opacity-20 group-hover:opacity-40 transition-opacity duration-500" />
                <div className="w-1.5 h-1.5 rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-600 animate-pulse" />
              </div>
            </div>
          </Card>
        </div>
        </div>

         {/* Orders List */}
      <div className="container mx-auto px-2 sm:px-0 py-2 flex-1 overflow-hidden flex flex-col">
        {isLoading ? (
          <Card className="shadow-card">
            <CardContent className="p-6 sm:p-8 text-center">
              <p className="text-muted-foreground text-sm sm:text-base">Loading orders...</p>
            </CardContent>
          </Card>
        ) : filteredOrders.length === 0 ? (
          <Card className="shadow-card">
            <CardContent className="p-6 sm:p-8 text-center">
              <p className="mb-4 text-muted-foreground text-sm sm:text-base">No orders found.</p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Mobile/Tablet Card View */}
            <div className="lg:hidden flex-1 overflow-auto">
              <div className="space-y-3 pb-4">
                {filteredOrders.map((order) => (
                  <Card key={order.orderNo} className="shadow-card hover:shadow-lg transition-shadow">
                    <CardContent className="p-4">
                      <div className="space-y-3">
                        {/* Header Row */}
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              {order.orderType && (
                                <Badge 
                                  variant="secondary" 
                                  className={`text-xs font-medium ${getOrderTypeBadgeClassName(order.orderType)}`}
                                >
                                  {order.orderType}
                                </Badge>
                              )}
                              <span className="font-bold text-primary text-base sm:text-lg">
                                ORD-{order.orderNo}
                              </span>
                              <Badge variant="outline" className="text-xs">
                                {order.items} {order.items === 1 ? 'item' : 'items'}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-2 min-w-0">
                              <span className="font-semibold text-sm sm:text-base truncate">{order.customer}</span>
                              {order.partyCode && (
                                <>
                                  <span className="text-xs text-muted-foreground shrink-0">•</span>
                                  <span className="text-xs text-muted-foreground shrink-0">{order.partyCode}</span>
                                </>
                              )}
                            </div>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewDetails(order)}
                            className="h-8 w-8 p-0 shrink-0"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </div>

                        {/* Details Grid */}
                        <div className="grid grid-cols-2 gap-3 pt-2 border-t">
                          <div className="space-y-1">
                            <p className="text-xs text-muted-foreground">Town</p>
                            <p className="text-sm font-medium">{order.town || "-"}</p>
                          </div>
                          <div className="space-y-1">
                            <p className="text-xs text-muted-foreground">Mobile</p>
                            <p className="text-sm font-medium">{order.mobile || "-"}</p>
                          </div>
                          <div className="space-y-1">
                            <p className="text-xs text-muted-foreground">Date & Time</p>
                            <p className="text-sm font-medium">{order.date} {order.time}</p>
                          </div>
                          <div className="space-y-1">
                            <p className="text-xs text-muted-foreground">Amount</p>
                            <p className="text-sm font-bold text-primary">{formatCurrency(order.amount)}</p>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Desktop Table View */}
            <Card className="hidden lg:flex shadow-card overflow-hidden flex-1 flex flex-col p-0">
              <div className="relative w-full flex-1 overflow-auto">
                <table className="w-full caption-bottom text-sm relative">
                  <thead>
                    <tr className="h-8 border-b">
                      <th className="w-[120px] bg-muted py-1 sticky top-0 z-10 h-12 px-4 text-left align-middle font-medium text-muted-foreground">Order Type</th>
                      <th className="w-[120px] bg-muted py-1 sticky top-0 z-10 h-12 px-4 text-left align-middle font-medium text-muted-foreground">Order No</th>
                      <th className="w-[600px] bg-muted py-1 sticky top-0 z-10 h-12 px-4 text-left align-middle font-medium text-muted-foreground">Customer</th>
                      <th className="w-[300px] bg-muted py-1 sticky top-0 z-10 h-12 px-2 text-left align-middle font-medium text-muted-foreground">Town</th>
                      <th className="w-[150px] bg-muted py-1 sticky top-0 z-10 h-12 px-2 text-left align-middle font-medium text-muted-foreground">Mobile No.</th>
                      <th className="w-[100px] bg-muted py-1 sticky top-0 z-10 h-12 px-4 text-center align-middle font-medium text-muted-foreground">Items</th>
                      <th className="w-[180px] bg-muted py-1 sticky top-0 z-10 h-12 px-4 text-left align-middle font-medium text-muted-foreground">Date & Time</th>
                      <th className="w-[150px] bg-muted py-1 sticky top-0 z-10 h-12 px-4 pr-6 text-right align-middle font-medium text-muted-foreground">Aprox Value</th>
                      <th className="w-[100px] bg-muted py-1 sticky top-0 z-10 h-12 px-4 text-center align-middle font-medium text-muted-foreground">View</th>
                    </tr>
                  </thead>
                  
                  <tbody>
                    {filteredOrders.map((order) => (
                      <tr
                        key={order.orderNo}
                        className="border-b transition-colors hover:bg-muted/50 h-10"
                      >
                        <td className="p-4 align-middle py-1 whitespace-nowrap">
                          {order.orderType ? (
                            <Badge 
                              variant="secondary" 
                              className={`text-xs font-medium ${getOrderTypeBadgeClassName(order.orderType)}`}
                            >
                              {order.orderType === "SalesMan"
                                ? (order.salesManInfo || "SalesMan")
                                : order.orderType}
                            </Badge>
                          ) : (
                            <span className="text-sm text-muted-foreground">-</span>
                          )}
                        </td>
                        <td className="p-4 align-middle py-1 whitespace-nowrap">
                          <span className="font-bold text-primary text-base">
                            ORD-{order.orderNo}
                          </span>
                        </td>
                        <td className="p-6 align-middle py-2 min-w-0">
                          <div className="flex items-center gap-2 whitespace-nowrap overflow-hidden">
                            <span className="font-semibold text-sm truncate">{order.customer}</span>
                            {order.partyCode && (
                              <>
                                <span className="text-xs text-muted-foreground shrink-0">•</span>
                                <span className="text-xs text-muted-foreground shrink-0">{order.partyCode}</span>
                              </>
                            )}
                          </div>
                        </td>
                        <td className="px-2 align-middle py-1">
                          <span className="text-sm text-muted-foreground">
                            {order.town || "-"}
                          </span>
                        </td>
                        <td className="px-2 align-middle py-1">
                          <span className="text-sm text-muted-foreground">
                            {order.mobile || "-"}
                          </span>
                        </td>
                        <td className="p-4 align-middle text-center py-1">
                          <span className="text-sm text-muted-foreground">
                            {order.items}
                          </span>
                        </td>
                        <td className="p-4 align-middle py-1">
                          <span className="text-sm whitespace-nowrap">{order.date} {order.time}</span>
                        </td>
                        <td className="p-4 pr-8 align-middle text-right py-1">
                          <span className="font-bold text-primary text-base">
                            {formatCurrency(order.amount)}
                          </span>
                        </td>
                        <td className="p-4 align-middle text-center py-1">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewDetails(order)}
                            className="h-7 w-7 p-0"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </>
        )}
      </div>

      {/* Order Details Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <DialogContent
        className="max-h-[90vh] max-w-[95vw] sm:max-w-2xl md:max-w-4xl overflow-hidden p-3 sm:p-4"
        aria-describedby="order-details-description"
      >
        <DialogHeader className="pb-2">
          <DialogTitle className="flex items-center gap-2 text-lg sm:text-xl">
            <Package className="h-4 w-4 sm:h-5 sm:w-5" />
            Order Details
          </DialogTitle>
          <p id="order-details-description" className="sr-only">
            Detailed information about the selected order including firm
            details, party information, product details, and pricing.
          </p>
        </DialogHeader>

        {isLoadingDetails ? (
          <div className="flex items-center justify-center py-8 sm:py-12">
            <Loader2 className="h-6 w-6 sm:h-8 sm:w-8 animate-spin" />
            <span className="ml-2 text-sm sm:text-base">Loading order details...</span>
          </div>
        ) : orderDetails && orderDetails.length > 0 ? (
          <div className="space-y-3 overflow-y-auto max-h-[calc(90vh-120px)] pr-1">
            {/* Order Header */}
            <Card>
               <CardContent className="pt-3 sm:pt-4 px-3 sm:px-4 pb-3 sm:pb-4">
                <div className="space-y-3 sm:space-y-2">
                  {/* Row 1: Order No and Order Date */}
                  <div className="grid grid-cols-1 gap-3 sm:gap-4 sm:grid-cols-2">
                    <div className="flex items-start sm:items-center gap-2 text-xs sm:text-sm">
                      <Hash className="h-3 w-3 sm:h-4 sm:w-4 text-muted-foreground mt-0.5 sm:mt-0 shrink-0" />
                      <div className="min-w-0">
                        <span className="font-medium">Order No:</span>
                        <span className="ml-1 break-all">{orderDetails[0].orderNo}</span>
                      </div>
                    </div>
                    <div className="flex items-start sm:items-center gap-2 text-xs sm:text-sm">
                      <Calendar className="h-3 w-3 sm:h-4 sm:w-4 text-muted-foreground mt-0.5 sm:mt-0 shrink-0" />
                      <div className="min-w-0">
                        <span className="font-medium">Order Date:</span>
                        <span className="ml-1 break-words">
                          {formatDate(orderDetails[0].orderDate)} at{" "}
                          {formatTime(orderDetails[0].orderDate)}
                        </span>
                      </div>
                    </div>
                  </div>
                  {/* Row 2: Party Name and Total Amount */}
                  <div className="grid grid-cols-1 gap-3 sm:gap-4 sm:grid-cols-2">
                    <div className="flex items-start sm:items-center gap-2 text-xs sm:text-sm">
                      <User className="h-3 w-3 sm:h-4 sm:w-4 text-muted-foreground mt-0.5 sm:mt-0 shrink-0" />
                      <div className="min-w-0">
                        <span className="font-medium">Party:</span>
                        <span className="ml-1 break-words">
                          {orderDetails[0].partyName} ({orderDetails[0].partyCode})
                        </span>
                      </div>
                    </div>
                    <div className="flex items-start sm:items-center gap-2 text-xs sm:text-sm">
                      <Wallet className="h-3 w-3 sm:h-4 sm:w-4 text-muted-foreground mt-0.5 sm:mt-0 shrink-0" />
                      <div className="min-w-0">
                        <span className="font-medium">Aprox Value:</span>
                        <span className="ml-1 text-base sm:text-lg font-bold text-primary break-words">
                          {formatCurrency(orderDetails[0].totalAmount)}
                        </span>
                      </div>
                    </div>
                  </div>
                  {/* Row 3: Town and Mobile Number */}
                  <div className="grid grid-cols-1 gap-3 sm:gap-4 sm:grid-cols-2">
                    <div className="flex items-start sm:items-center gap-2 text-xs sm:text-sm">
                      <MapPin className="h-3 w-3 sm:h-4 sm:w-4 text-muted-foreground mt-0.5 sm:mt-0 shrink-0" />
                      <div className="min-w-0">
                        <span className="font-medium">Town:</span>
                        <span className="ml-1 break-words">{orderDetails[0].city || "-"}</span>
                      </div>
                    </div>
                    <div className="flex items-start sm:items-center gap-2 text-xs sm:text-sm">
                      <Phone className="h-3 w-3 sm:h-4 sm:w-4 text-muted-foreground mt-0.5 sm:mt-0 shrink-0" />
                      <div className="min-w-0">
                        <span className="font-medium">Mobile No.:</span>
                        <span className="ml-1 break-words">{orderDetails[0].mobileNumber || "-"}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Products Information */}
            <Card>
              <CardHeader className="pb-2 pt-3 px-3 sm:px-4">
                <CardTitle className="flex items-center gap-2 text-sm sm:text-base">
                  <Package className="h-3 w-3 sm:h-4 sm:w-4" />
                  Products Information ({orderDetails.length} items)
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0 px-3 sm:px-4 pb-3 sm:pb-4">
                {/* Mobile/Tablet Card View */}
                <div className="lg:hidden space-y-3 max-h-[300px] overflow-y-auto">
                  {orderDetails.map((product, index) => (
                    <Card key={index} className="border">
                      <CardContent className="p-3">
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-muted-foreground">#{index + 1}</span>
                            <Badge variant="outline" className="text-xs">
                              {product.qty} Qty
                            </Badge>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Product Code</p>
                            <p className="text-sm font-medium">{product.productCode}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Product Name</p>
                            <p className="text-sm font-semibold">{product.productName}</p>
                          </div>
                          <div className="grid grid-cols-2 gap-2 pt-2 border-t">
                            <div>
                              <p className="text-xs text-muted-foreground">Free</p>
                              <p className="text-sm">{product.free}</p>
                            </div>
                            <div>
                              <p className="text-xs text-muted-foreground">Rate</p>
                              <p className="text-sm">{formatCurrency(product.rate)}</p>
                            </div>
                            <div className="col-span-2">
                              <p className="text-xs text-muted-foreground">Value</p>
                              <p className="text-sm font-bold text-primary">{formatCurrency(product.amount)}</p>
                            </div>
                            {product.scheme && (
                              <div className="col-span-2">
                                <p className="text-xs text-muted-foreground">Scheme</p>
                                <p className="text-sm">{product.scheme}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* Desktop Table View */}
                <div className="hidden lg:block max-h-[300px] overflow-y-auto overflow-x-auto border rounded-md">
                  <table className="w-full caption-bottom text-sm relative">
                    <thead>
                      <tr className="h-8 border-b">
                        <th className="w-[50px] bg-muted py-1 sticky top-0 z-10 h-12 px-4 text-left align-middle font-medium text-muted-foreground">
                          #
                        </th>
                        <th className="w-[120px] bg-muted py-1 sticky top-0 z-10 h-12 px-4 text-left align-middle font-medium text-muted-foreground">
                          Product Code
                        </th>
                        <th className="w-[250px] bg-muted py-1 sticky top-0 z-10 h-12 px-4 text-left align-middle font-medium text-muted-foreground">
                          Product Name
                        </th>
                        <th className="w-[80px] text-center bg-muted py-1 sticky top-0 z-10 h-12 px-4 align-middle font-medium text-muted-foreground">
                          Qty
                        </th>
                        <th className="w-[80px] text-center bg-muted py-1 sticky top-0 z-10 h-12 px-4 align-middle font-medium text-muted-foreground">
                          Free
                        </th>
                        <th className="w-[150px] text-center bg-muted py-1 sticky top-0 z-10 h-12 px-4 align-middle font-medium text-muted-foreground">
                          Rate
                        </th>
                        <th className="w-[180px] text-center bg-muted py-1 sticky top-0 z-10 h-12 px-4 align-middle font-medium text-muted-foreground">
                        Value
                        </th>
                        <th className="w-[150px] bg-muted py-1 sticky top-0 z-10 h-12 px-4 text-left align-middle font-medium text-muted-foreground">
                          Scheme
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {orderDetails.map((product, index) => (
                        <tr key={index} className="border-b transition-colors hover:bg-muted/50 h-10">
                          <td className="p-4 align-middle py-1">
                            <span className="text-sm">{index + 1}</span>
                          </td>
                          <td className="p-4 align-middle py-1">
                            <span className="text-sm">{product.productCode}</span>
                          </td>
                          <td className="p-4 align-middle py-1">
                            <span className="font-semibold text-sm">{product.productName}</span>
                          </td>
                          <td className="p-4 align-middle text-right py-1 pr-8">
                            <span className="font-bold text-sm">{product.qty}</span>
                          </td>
                          <td className="p-4 align-middle text-center py-1">
                            <span className="text-sm">{product.free}</span>
                          </td>
                          <td className="p-4 pr-8 align-middle text-right py-1">
                            <span className="text-sm">{formatCurrency(product.rate)}</span>
                          </td>
                          <td className="p-4 pr-8 align-middle text-right py-1">
                            <span className="font-bold text-primary text-base">
                              {formatCurrency(product.amount)}
                            </span>
                          </td>
                          <td className="p-4 align-middle py-1">
                            <span className="text-sm">{product.scheme || "-"}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        ) : (
          <div className="py-8 sm:py-12 text-center">
            <p className="text-muted-foreground text-sm sm:text-base">No order details available</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
      </div>
    </Layout>
  );
};

export default Orders;
